import React, { useState, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { auth } from './firebaseConfig';
import {
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile
} from 'firebase/auth';
import { requestVerificationEmail, requestPasswordReset } from './services/emailActionsService';
import { getPasswordStrength, MIN_REQUIRED_PASSWORD_SCORE } from './utils/passwordStrength';
import { SessionProvider } from './context/SessionContext';
import { ProfileProvider } from './context/ProfileContext';
import { CartProvider } from './context/CartContext';
import { TabProvider } from './context/TabContext';
import RoutinePicker from './components/RoutinePicker';
import ProfileGate from './components/ProfileGate';
import UsernameGate from './components/UsernameGate';
import EmailVerificationGate from './components/EmailVerificationGate';
import ProfileSwitcher from './components/ProfileSwitcher';
import ActiveSessionsBar from './components/ActiveSessionsBar';
import Contact from './components/Contact';
import WorkoutLog from './components/WorkoutLog';
import Shop from './components/Shop';
import TabBar from './components/TabBar';
import FloatingCart from './components/FloatingCart';
import DeleteAccount from './components/DeleteAccount';
import AuthAction from './components/AuthAction';
import GuidedTour from './components/GuidedTour';
import IconButton from './components/IconButton';
import { friendlyAuthError } from './utils/friendlyAuthError';
import './components/comic.css';

// Low-key text-link for mode switches ("Sign Up instead" / "Forgot password?").
// Not a .comic-btn — those are the primary CTA; these are secondary navigation,
// so a plain underlined link keeps the visual hierarchy clear.
const linkStyle = {
  background: "none",
  border: "none",
  padding: 0,
  color: "#232526",
  fontFamily: "'Anonymous Pro', monospace",
  fontSize: "14px",
  textDecoration: "underline",
  cursor: "pointer"
};

// Live password-strength feedback shown under the password field during
// signup, driven by utils/passwordStrength.js's heuristic. A 4-segment bar
// (filled count = score) rather than a continuous percentage, since the
// score is itself a discrete 0-4 scale, not a true percentage -- a bar
// that implied finer granularity than the heuristic actually has would be
// misleading.
const PasswordStrengthMeter = ({ password }) => {
  const { score, label, color } = getPasswordStrength(password);
  return (
    <div style={{ marginBottom: "14px" }}>
      <div style={{ display: "flex", gap: "4px", marginBottom: "4px" }}>
        {[1, 2, 3, 4].map((segment) => (
          <div
            key={segment}
            style={{
              flex: 1,
              height: "6px",
              borderRadius: "3px",
              background: segment <= score ? color : "#e0e0e0"
            }}
          />
        ))}
      </div>
      <span style={{ fontFamily: "'Anonymous Pro', monospace", fontSize: "12px", color }}>
        {label}
      </span>
    </div>
  );
};

// Single Login component covering three states: "login", "signup", and "reset".
// Log In / Sign Up share the same email + password inputs and only swap which
// Firebase call + button label are used; Reset is a minimal email-only view.
function Login({ onSignedUp }) {
  const [mode, setMode] = useState("login"); // "login" | "signup" | "reset"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Clear any lingering error/confirmation when switching between states.
  const switchMode = (next) => {
    setMode(next);
    setError(null);
    setNotice(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      if (mode === "signup") {
        const trimmedUsername = username.trim();
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(cred.user, { displayName: trimmedUsername });
        // Per direct user request ("I don't want people using fake email
        // addresses... let them know if there is an issue"): every new
        // signup gets a verification link, and AppContent's EmailVerification
        // gate blocks entry until it's confirmed clicked. Routed through a
        // Cloud Function (not the client SDK's own sendEmailVerification)
        // so the email comes from info@pumpous.com instead of Firebase's
        // generic shared sender -- see emailActionsService.js.
        const verifyResult = await requestVerificationEmail();
        if (!verifyResult.success) throw new Error(verifyResult.error);
        // Seed the parent's displayName state directly rather than waiting
        // on the auth-state listener to catch up -- avoids a race where
        // UsernameGate would briefly (and redundantly) ask again for a name
        // we just collected right here.
        onSignedUp(trimmedUsername);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
      // On success the auth listener swaps this screen out; no local nav needed.
    } catch (err) {
      // err.code means a real Firebase auth/* error (map via
      // friendlyAuthError); no code but a message means it came from the
      // requestVerificationEmail() throw above, which is already friendly.
      setError(err.code ? friendlyAuthError(err) : (err.message || friendlyAuthError(err)));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setNotice(null);
    // Don't hand Firebase an empty email — validate locally first.
    if (!email.trim()) {
      setError("Enter your email address first.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await requestPasswordReset(email.trim());
      if (!result.success) throw new Error(result.error);
      setNotice("Check your email for a reset link.");
    } catch (err) {
      setError(err.code ? friendlyAuthError(err) : (err.message || friendlyAuthError(err)));
    } finally {
      setSubmitting(false);
    }
  };

  if (mode === "reset") {
    return (
      <div className="comic-panel comic-panel--outer-active">
        <h2 className="comic-title" style={{ fontSize: "26px" }}>Reset Your Password</h2>
        <p className="comic-subtitle">
          Enter your email and we'll send you a link to get back in.
        </p>
        <form onSubmit={handleReset}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="comic-input"
            style={{ width: "100%" }}
            autoFocus
          />
          <button
            type="submit"
            disabled={submitting}
            className="comic-btn comic-btn--go"
          >
            {submitting ? "Sending..." : "Send Reset Link"}
          </button>
          {error && <div className="comic-alert-bubble">{error}</div>}
          {notice && <div className="comic-alert-bubble">{notice}</div>}
        </form>
        <div style={{ marginTop: "16px" }}>
          <button type="button" onClick={() => switchMode("login")} style={linkStyle}>
            Back to Log In
          </button>
        </div>
      </div>
    );
  }

  const isSignup = mode === "signup";

  return (
    <div className="comic-panel comic-panel--outer-active">
      <h2 className="comic-title" style={{ fontSize: "26px" }}>
        {isSignup ? "Join Pumpous" : "Log In to Pumpous"}
      </h2>
      <p className="comic-subtitle">
        {isSignup
          ? "Create your account and start training like a Hero."
          : "Welcome back, Hero. Log in to continue your mission."}
      </p>
      <form onSubmit={handleSubmit}>
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="comic-input"
          style={{ width: "100%", marginBottom: "14px" }}
          autoFocus
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="comic-input"
          style={{ width: "100%", marginBottom: isSignup && password ? "6px" : (isSignup ? "14px" : 0) }}
        />
        {isSignup && password && <PasswordStrengthMeter password={password} />}
        {isSignup && (
          <input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="comic-input"
            style={{ width: "100%" }}
            maxLength={40}
          />
        )}
        <button
          type="submit"
          disabled={submitting || (isSignup && !username.trim()) || (isSignup && getPasswordStrength(password).score < MIN_REQUIRED_PASSWORD_SCORE)}
          className="comic-btn comic-btn--go"
        >
          {submitting
            ? (isSignup ? "Creating..." : "Logging in...")
            : (isSignup ? "Sign Up" : "Log In")}
        </button>
        {error && <div className="comic-alert-bubble">{error}</div>}
      </form>

      <div style={{ marginTop: "16px", display: "flex", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => switchMode(isSignup ? "login" : "signup")}
          style={linkStyle}
        >
          {isSignup ? "Already have an account? Log In" : "New here? Sign Up"}
        </button>
        {!isSignup && (
          <button type="button" onClick={() => switchMode("reset")} style={linkStyle}>
            Forgot password?
          </button>
        )}
      </div>
    </div>
  );
}

function AppContent() {
  const [user, setUser] = useState(null);
  // Local override for the account's display name -- set directly by
  // signup (Login's onSignedUp) or UsernameGate, so the UI reflects a
  // just-chosen username immediately rather than waiting on Firebase's
  // auth-state listener to catch up (which, since it hands back the same
  // mutated User object reference, wouldn't reliably trigger a re-render
  // on its own). Reset on logout so a stale name from a previous account
  // can't leak into a different account's session on the same device.
  const [localDisplayName, setLocalDisplayName] = useState(null);
  const displayName = localDisplayName || user?.displayName || null;

  // Local override for email-verified status, same "same mutated User
  // object reference doesn't reliably trigger a re-render" reasoning as
  // localDisplayName above -- EmailVerificationGate calls auth.currentUser
  // .reload() then reads .emailVerified directly rather than waiting on
  // onAuthStateChanged to fire again (it may not).
  const [localEmailVerified, setLocalEmailVerified] = useState(false);
  const emailVerified = localEmailVerified || user?.emailVerified || false;

  // Fixed cutoff (not a rolling "created in the last N hours" window): any
  // account created before this feature shipped is exempt from the check
  // forever -- verification wasn't required when they signed up, and
  // retroactively locking out an existing alpha tester's own real email over
  // a gate that didn't exist yet isn't what was asked for (the request was
  // scoped to "setting up their profile" and "updating their email", i.e.
  // signup and email-change, not an audit of every existing account).
  // Anything created from this timestamp onward must verify, with no decay
  // -- unlike a rolling window, someone can't dodge it forever just by
  // waiting a day before reopening the app.
  const EMAIL_VERIFICATION_REQUIRED_SINCE = new Date('2026-08-13T00:00:00Z').getTime();
  const createdAtMs = user?.metadata?.creationTime
    ? new Date(user.metadata.creationTime).getTime()
    : null;
  const needsEmailVerification =
    !emailVerified && createdAtMs !== null && createdAtMs >= EMAIL_VERIFICATION_REQUIRED_SINCE;

  // Which of the 5 top-level tabs is showing (see TabBar.jsx/TabContext.jsx).
  // "routines" (Select Mission) is the default/home tab per direct request.
  const [activeTab, setActiveTab] = useState("routines");

  // Tabs stay mounted and are just shown/hidden via CSS (see App.js's
  // display:none/block wrappers below) -- switching tabs never remounts
  // anything, so the scroll position of the tab-content pane carries over
  // unchanged. Real bug this caused, reported by the user ("the links at
  // the bottom are missing on every tab except Exercises"): scroll deep
  // into a tall tab (e.g. Shop's product list), then tap a much shorter
  // tab (e.g. Quick Start) -- the retained scroll offset lands the
  // viewport well past that shorter tab's actual content, showing blank
  // space with the footer sitting ABOVE the current view, not below it,
  // so it reads as "missing" rather than "just needs scrolling up."
  // Resetting scroll to the top on every tab change makes each tab always
  // start at its own top, so its footer is reliably reachable by
  // scrolling down, same as before tabs existed. Scrolls `contentScrollRef`
  // (the tab-content pane's own independent scroll container -- see its
  // declaration/JSX below), not `window` -- the page itself no longer
  // scrolls at all once tabs are showing.
  const contentScrollRef = useRef(null);
  useEffect(() => {
    if (contentScrollRef.current) contentScrollRef.current.scrollTop = 0;
  }, [activeTab]);

  // Styling constants
  const mobileButtonStyle = { flex: 1, height: "48px", backgroundColor: "#333", color: "#fff", fontSize: "16px", borderRadius: "8px", cursor: "pointer", border: "none" };

  // Auth Listener
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(setUser);
    return () => unsubscribe();
  }, []);

  // iOS-readiness: the native status bar is a system UI element outside the
  // WebView, so it needs its own explicit style/color call -- it doesn't
  // just inherit the page's dark background from CSS. Style.Dark = light
  // (white) status bar text/icons, the correct choice against this app's
  // dark Primary (#232526) background/header. No-ops harmlessly on plain
  // web (Capacitor.isNativePlatform() is false there), so this file still
  // works unchanged for the browser build.
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      StatusBar.setStyle({ style: Style.Dark });
      StatusBar.setBackgroundColor({ color: "#232526" });
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setLocalDisplayName(null);
      setLocalEmailVerified(false);
      setActiveTab("routines");
    }
  }, [user]);

  // Only the authenticated, past-every-gate tree gets the tab bar's bottom
  // padding -- Login/UsernameGate/EmailVerificationGate have no TabBar, so
  // padding for one would just be empty dead space.
  const showTabs = user && displayName && !needsEmailVerification;

  // Guided onboarding tour (GuidedTour.jsx) -- per direct user request,
  // starts automatically the first time an account reaches the dashboard.
  // Tracked per-account via localStorage (pumpous.tourSeen.{uid}, the same
  // scoping convention ProfileContext/CartContext already use for their own
  // per-account flags) rather than a new Firestore collection -- this is a
  // one-way "have they seen it" flag, not data that needs to sync across
  // devices, so the simpler client-only mechanism is enough; a user signing
  // in fresh on a second device seeing the tour again is a reasonable,
  // low-stakes outcome, not a bug. Since there's no signup-date cutoff
  // (unlike EmailVerificationGate's deliberate one above), every existing
  // account will also see it once on their next login after this ships --
  // acceptable here since the tour is purely informational and skippable,
  // not a blocking gate.
  const [tourActive, setTourActive] = useState(false);
  const tourSeenKey = user ? `pumpous.tourSeen.${user.uid}` : null;

  // Which Hero's Cancel-Mission dialog, if any, GuidedTour is currently
  // waiting on the real user to tap Discard for (see its "Cancelling a
  // Mission"/"Cleaning Up" steps and ActiveMission.jsx's lockCancelConfirm
  // prop) -- lifted up here since GuidedTour and RoutinePicker are siblings,
  // not parent/child, so this is the lowest common point to pass it through.
  const [tourLockedProfileId, setTourLockedProfileId] = useState(null);

  useEffect(() => {
    if (!showTabs || !tourSeenKey) return;
    if (localStorage.getItem(tourSeenKey)) return;
    setTourActive(true);
    // Only re-check when showTabs first flips true (or a different account
    // logs in) -- not on every render, so dismissing the tour (which
    // doesn't change showTabs) can't retrigger it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTabs, tourSeenKey]);

  const finishTour = () => {
    setTourActive(false);
    if (tourSeenKey) localStorage.setItem(tourSeenKey, "1");
  };

  if (!showTabs) {
    return (
      // minHeight:100vh + its own explicit white background -- body's own
      // background is dark Primary now (index.css, see its comment), used
      // specifically so WebKit fills the notch/Dynamic Island "unsafe
      // area" with that dark color instead of white. This wrapper spans
      // the FULL viewport (not just the narrower centered column below)
      // so none of that dark body shows through as a visual regression on
      // the sides or below short content -- the actual content keeps its
      // existing narrower, padded, centered-column treatment nested
      // inside, unchanged. `overflow: "hidden"` is load-bearing, not
      // decorative -- without it, the inner column's own `margin: "8px
      // auto"` collapses straight through this wrapper AND through body
      // itself (nothing on either blocks a first-child's top margin from
      // escaping), shifting body's own rendered box down by that same
      // 8px and leaving a thin sliver of its now-dark background visible
      // above this wrapper -- confirmed via a direct pixel/rect check
      // before landing on this fix, not assumed. `overflow: hidden`
      // establishes a new block formatting context, which is the
      // standard, minimal way to contain a child's margin instead.
      <div style={{ minHeight: "100vh", background: "#ffffff", overflow: "hidden" }}>
        <div
          style={{
            paddingTop: "8px",
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingBottom: "20px",
            maxWidth: "500px",
            margin: "8px auto"
          }}
        >
          {user ? (
            !displayName ? (
              <UsernameGate onSet={setLocalDisplayName} />
            ) : (
              <EmailVerificationGate onVerified={() => setLocalEmailVerified(true)} />
            )
          ) : (
            <Login onSignedUp={setLocalDisplayName} />
          )}
          {/* Once tabs exist, Privacy/Terms/Delete Account move into
              ProfileSwitcher's expanded panel instead (per direct user
              request) -- this footer only covers the states handled in
              this branch: logged out, or logged in but not yet past
              UsernameGate/EmailVerificationGate (neither renders
              ProfileSwitcher, so Delete Account still needs a way to be
              reachable here). */}
          <footer style={{ marginTop: "32px", textAlign: "center", fontSize: "12px", color: "#888" }}>
            <a href="/privacy.html" style={{ color: "#888" }}>Privacy Policy</a>
            {" · "}
            <a href="/terms.html" style={{ color: "#888" }}>Terms of Service</a>
            {user && (
              <>
                {" · "}
                <DeleteAccount />
              </>
            )}
          </footer>
        </div>
      </div>
    );
  }

  return (
    <TabProvider value={activeTab}>
    <CartProvider ownerUserId={user.uid}>
    <ProfileProvider ownerUserId={user.uid}>
    <ProfileGate ownerUserId={user.uid}>
    {/* [2026-10-20] Reworked from a `position: fixed` header over a
        normally-scrolling page into a true two-pane layout: this OUTER
        div is itself `position: fixed; inset: 0` (so it never moves,
        full stop, regardless of anything inside it), a flex column
        holding the header (a perfectly ordinary, non-fixed flex item) on
        top and a SEPARATE, independently-scrolling pane below it. Per
        direct user report with two real-device screenshots: the
        PREVIOUS fixed-header-over-a-scrolling-page approach looked
        correct in every test this environment's Chromium-based tooling
        could run, yet on a real device, scrolling could leave scrolled
        tab content rendering OVER the safe-area/Dynamic Island strip
        with the header itself landing in the wrong place -- a
        `position: fixed` element whose ANCESTOR page is also actively,
        natively scrolling is a well-documented category of WKWebView
        bug (the OS has to keep recomputing the fixed element's effective
        screen position in sync with the page's own scroll offset, which
        WKWebView has long-standing, known issues reconciling, especially
        under fast/momentum scrolling) -- consistent with this project's
        now-repeated lesson that an exotic technique working flawlessly
        in this environment's Chromium testing is no guarantee it works
        on a real WebKit device. Restructuring so the PAGE ITSELF never
        scrolls at all removes the bug's entire precondition rather than
        chasing another CSS tweak to it: with nothing at the document
        level scrolling, there's no scroll offset for this fixed outer
        shell to ever need to resync against -- it's positioned once,
        relative to the viewport, and simply never needs to move again.
        All ACTUAL scrolling now happens inside the nested `overflow-y:
        auto` pane below, a plain, broadly-supported CSS feature with no
        comparable WKWebView bug history. This also fully retires the
        previous approach's ResizeObserver-based `headerHeight`
        measurement (and the real bug it had to work around, a
        stuck-at-0 measurement caused by ProfileGate's own async loading
        delay) -- the browser's own flex layout now sizes both panes
        correctly with zero JS measurement of any kind. */}
    <div
      style={{
        position: "fixed",
        // `top: env(safe-area-inset-top)` (not `inset: 0` + `paddingTop`)
        // -- a real bug caught via the user's own screenshot: `inset: 0`
        // makes this shell's box (and therefore its white `background`,
        // which paints the padding box too, not just the content box)
        // start at the TRUE top of the screen, y=0, with `paddingTop`
        // only pushing its CONTENT down -- so the safe-area strip itself
        // was still getting painted solid white, covering the native
        // app's own dark background (capacitor.config.json's
        // backgroundColor) that's supposed to show through there and
        // keep the status bar icons legible. Starting the box itself
        // exactly at the safe-area boundary means its background can
        // never paint above that line at all, regardless of padding.
        top: "env(safe-area-inset-top)",
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        flexDirection: "column",
        background: "#ffffff"
      }}
    >
      {/* Header: Welcome/tour-icon row, Log Out/Contact row, Hero
          switcher, quick-switch bar -- a perfectly ordinary flex item
          now, no position/top/left/right/z-index of its own at all.
          `flexShrink: 0` keeps it at its own natural (content-driven,
          variable -- the Hero roster can expand, ActiveSessionsBar
          appears/disappears) height regardless of how little space is
          left for the scrollable pane below it. */}
      {/* boxSizing: "border-box" is load-bearing here, not decorative --
          real bug caught via direct measurement (scrollWidth/getBoundingClientRect,
          not just a screenshot read), reported by the user as "the top
          elements... go off screen on the right side": with the browser's
          default content-box sizing, `width: "100%"` and the 20px left/right
          padding below stack ADDITIVELY -- this div rendered 40px wider than
          its own fixed-shell parent (430px against a 390px viewport), and
          every child measuring itself against "100% of this div" (the
          ProfileSwitcher account-row panel/button, the "N / N Heroes" chip)
          inherited that same 40px of extra width, pushing their right edges
          off the true screen edge. border-box makes the specified width
          include the padding instead of adding to it, so this div's
          rendered box is genuinely 100% of its parent -- no wider -- exactly
          like every other width+padding element in this codebase already
          gets via .comic-panel/.comic-input/.comic-btn's own shared
          box-sizing:border-box rule (comic.css); this is a plain inline-
          styled div with no such class, so it needed the same property set
          directly. */}
      <div style={{ flexShrink: 0, maxWidth: "500px", width: "100%", margin: "0 auto", paddingTop: "10px", paddingLeft: "20px", paddingRight: "20px", boxSizing: "border-box" }}>
        {/* paddingRight lines the icon's right edge up with the delete
            icon column in "Select Mission" below -- without it,
            space-between alone pushes the icon flush to this wrapper's
            own edge, which sits further right than a panel's internal
            content padding does (measured live: a 24px gap between the
            two, matching the category panel's own right-side padding). */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", paddingRight: "24px" }}>
          <h2 style={{ margin: 0 }}>Welcome, {displayName}</h2>
          <IconButton icon="map" onClick={() => setTourActive(true)} color="#00a274" label="Take the guided tour" size={32} />
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", margin: "10px 0px" }}>
          <button onClick={() => signOut(auth)} style={mobileButtonStyle}>Log Out</button>
          <Contact />
        </div>
        <ProfileSwitcher onUsernameChanged={setLocalDisplayName} />
        <ActiveSessionsBar onJumpToSession={() => setActiveTab("routines")} />
      </div>

      {/* The scrollable pane: `flex: 1` claims whatever space the header
          above didn't use, `overflow-y: auto` makes THIS div (not the
          page) the thing that actually scrolls. `comic-app-content--with-
          tabbar` supplies the existing bottom clearance for the fixed
          TabBar (`calc(76px + env(safe-area-inset-bottom))`) -- its
          previous home, the page-level wrapper, no longer scrolls at
          all, so this is the one place that clearance is still needed. */}
      <div
        ref={contentScrollRef}
        className="comic-app-content--with-tabbar"
        style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", overscrollBehaviorY: "none" }}
      >
        {/* [2026-10-20] Horizontal padding/maxWidth/centering moved off the
            scroll pane above and onto this div instead, using the EXACT
            same formula (width:100% + maxWidth:500 + margin:0 auto +
            paddingLeft/Right:20 + boxSizing:border-box) as the header
            wrapper div above -- per direct user report with a real-device
            screenshot that the category panels' right edge (e.g. "Select
            Mission") didn't line up with the header's right edge (the Hero
            switcher panel, Contact button), despite both measuring
            byte-identical in this environment's Chromium-based testing
            (370px, out of a 390px viewport, on both). The previous version
            computed this content area's width via a DIFFERENT CSS path than
            the header -- the scroll pane's own flex-stretch sizing (cross-
            axis stretch to fill its flex container, then padding subtracted)
            feeding into a separately-nested maxWidth:500/margin:auto block
            with no explicit width of its own -- versus the header wrapper's
            single explicit width:100%+maxWidth+padding+border-box
            computation. Two different layout algorithms arriving at
            (nominally) the same number is exactly the kind of place a real
            WebKit sub-pixel rounding quirk can show up without ever
            reproducing in this project's Chromium-only testing -- this
            project has hit that exact class of real-device-only
            discrepancy several times already today. Using the identical
            formula at the same nesting depth relative to the same 390px-
            wide ancestor (the fixed shell) removes the asymmetry rather
            than patching around whatever the specific rounding difference
            turns out to be. */}
        <div style={{ maxWidth: "500px", width: "100%", margin: "0 auto", paddingLeft: "20px", paddingRight: "20px", boxSizing: "border-box" }}>
          <div style={{ display: activeTab === "routines" ? "block" : "none" }}>
            <RoutinePicker userId={user.uid} tourLockedProfileId={tourLockedProfileId} />
          </div>

          <WorkoutLog userId={user.uid} activeTab={activeTab} />

          <div style={{ display: activeTab === "shop" ? "block" : "none" }}>
            <Shop isActive={activeTab === "shop"} />
          </div>
        </div>
      </div>
    </div>
    <FloatingCart />
    <TabBar activeTab={activeTab} onSelect={setActiveTab} />
    <GuidedTour active={tourActive} activeTab={activeTab} onTabChange={setActiveTab} onFinish={finishTour} userId={user.uid} onLockProfile={setTourLockedProfileId} />
    </ProfileGate>
    </ProfileProvider>
    </CartProvider>
    </TabProvider>
  );
}

function App() {
  // Password-reset / verify-email / change-email links (functions/index.js's
  // sendPasswordResetLink/sendVerificationEmail/sendChangeEmailVerification,
  // each minting their own token -- not a Firebase oobCode) land the user
  // here with these two query params, straight from the email's link click
  // -- never from in-app navigation. Checked before any provider/gate below,
  // since whoever's here may not even be signed in on this device at all.
  const params = new URLSearchParams(window.location.search);
  const action = params.get('action');
  const token = params.get('token');
  if (action && token) {
    return <AuthAction action={action} token={token} />;
  }

  return (
    <SessionProvider>
      <AppContent />
    </SessionProvider>
  );
}

export default App;